import { ApolloError, NetworkStatus } from '@apollo/client';
import { InvoiceSummaryBucketFilter } from '@finance-hub/domain/invoicing/blocs/types/unpaid-invoice-filters';
import {
  serializeUnpaidInvoiceBucketFilter,
  serializeUnpaidInvoiceFilters,
  serializeUnpaidInvoiceSearch,
  serializeUnpaidInvoiceSort,
  transformUnpaidInvoiceListItem,
} from '@finance-hub/domain/invoicing/blocs/transformers/unpaid-invoice-list-transformer';
import {
  UnpaidInvoiceFilters,
  UnpaidInvoiceRow,
  UnpaidInvoiceSort,
} from '@finance-hub/domain/invoicing/blocs/types/unpaid-invoice-list';
import { useGeneratedFinanceHubUnpaidInvoiceListQuery } from '@finance-hub/domain/invoicing/blocs/documents/unpaid-invoice-list.generated';
import { useRef } from 'react';

const PAGE_SIZE = 50;

interface UseUnpaidInvoiceListLoaderArgs {
  bucketFilter: InvoiceSummaryBucketFilter;
  filters: UnpaidInvoiceFilters;
  search: string;
  sort: UnpaidInvoiceSort;
}

interface UseUnpaidInvoiceListLoaderResult {
  error: ApolloError | undefined;
  hasMore: boolean;
  loading: boolean;
  loadingMore: boolean;
  loadMore: () => Promise<void>;
  refetch: () => void;
  rows: UnpaidInvoiceRow[];
}

export const useUnpaidInvoiceListLoader = ({
  bucketFilter,
  filters,
  search,
  sort,
}: UseUnpaidInvoiceListLoaderArgs): UseUnpaidInvoiceListLoaderResult => {
  const isFetchingRef = useRef(false);

  const bucketParams = serializeUnpaidInvoiceBucketFilter(bucketFilter);
  const dialogParams = serializeUnpaidInvoiceFilters(filters);

  // The Total Credits card and the Filters dialog both narrow record type. No
  // record satisfies two different ones, so the list is empty without asking.
  const hasUnsatisfiableRecordType =
    bucketParams.record_type !== undefined &&
    dialogParams.record_type !== undefined &&
    bucketParams.record_type !== dialogParams.record_type;

  const { data, error, fetchMore, networkStatus, refetch } =
    useGeneratedFinanceHubUnpaidInvoiceListQuery({
      fetchPolicy: 'cache-and-network',
      nextFetchPolicy: () => 'cache-first',
      notifyOnNetworkStatusChange: true,
      skip: hasUnsatisfiableRecordType,
      variables: {
        limit: PAGE_SIZE,
        offset: 0,
        overdue_bucket: bucketParams.overdue_bucket,
        record_type: dialogParams.record_type ?? bucketParams.record_type,
        search_input: serializeUnpaidInvoiceSearch(search),
        ...serializeUnpaidInvoiceSort(sort),
      },
    });

  const page = data?.getUnpaidInvoiceList;
  const rows = page?.invoices.map(transformUnpaidInvoiceListItem) ?? [];
  const hasMore = page?.has_more_pages ?? false;
  const loadingMore = networkStatus === NetworkStatus.fetchMore;

  const loadMore = async () => {
    if (isFetchingRef.current || !hasMore) {
      return;
    }

    isFetchingRef.current = true;
    try {
      // Offset is the accumulated row count: the cache merges each page onto
      // the previous ones (see the getUnpaidInvoiceList type policy).
      await fetchMore({ variables: { offset: rows.length } });
    } catch {
      // A failed page load already surfaces through the query's error state;
      // catching only prevents an unhandled rejection (callers don't await)
    } finally {
      isFetchingRef.current = false;
    }
  };

  return {
    error,
    hasMore,
    loading:
      !hasUnsatisfiableRecordType &&
      !page &&
      networkStatus !== NetworkStatus.error,
    loadingMore,
    loadMore,
    refetch: () => void refetch(),
    rows,
  };
};
